import React, { useEffect, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import RegisterNotes, { humanizeCode } from './RegisterNotes';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { StatutoryRegisterService } from '../../services/statutoryRegister.service';
import { IInsuranceRegister } from '../../interfaces/statutoryRegister.interface';
import { formatDateTime, toLocalIsoDate } from '../../utils/dateUtils';
import { useToast } from '../../context/ToastContext';
import { dash, money, triggerDownload } from './shared';

interface Props {
    institutionId: string;
}

/**
 * Seventh statutory register tab: group insurance (BLA §99), spec
 * {@code docs/decisions/2026-09-08-group-insurance.md} D5 + REV 2. No year/month parameter — this
 * register is always "as of now" (R5). Caveats ALWAYS render (same convention as the WPPF tab).
 */
const InsuranceRegisterTab: React.FC<Props> = ({ institutionId }) => {
    const toast = useToast();
    const [register, setRegister] = useState<IInsuranceRegister | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const data = await StatutoryRegisterService.getInsuranceRegister(institutionId);
            setRegister(data);
        } catch (e) {
            setRegister(null);
            toast.showApiError(e, 'Failed to load insurance register');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const blob = await StatutoryRegisterService.downloadInsuranceRegisterXlsx(institutionId);
            triggerDownload(blob, `insurance-register-${toLocalIsoDate()}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadPdf = async () => {
        setDownloadingPdf(true);
        try {
            const blob = await StatutoryRegisterService.downloadInsuranceRegisterPdf(institutionId);
            triggerDownload(blob, `insurance-register-${toLocalIsoDate()}.pdf`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloadingPdf(false);
        }
    };

    const statusColor = (status: string): 'success' | 'warning' | 'default' => {
        if (status === 'ACTIVE') return 'success';
        if (status === 'EXPIRED') return 'warning';
        return 'default';
    };

    return (
        <Box data-help-page="statutory.insurance.page">
            <Box data-help-section="statutory.insurance.toolbar" display="flex" gap={2} alignItems="flex-end" mb={2} flexWrap="wrap">
                <Button data-help="statutory.insurance.refresh" variant="outlined" size="small" onClick={load} disabled={loading}>Refresh</Button>
                <Button data-help="statutory.insurance.downloadXlsx"
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownload} disabled={!register || downloading}
                >
                    Download XLSX
                </Button>
                <Button data-help="statutory.insurance.downloadPdf"
                    variant="outlined" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownloadPdf} disabled={!register || downloadingPdf}
                >
                    Download PDF
                </Button>
            </Box>

            {loading && <CircularProgress size={24} />}

            {!loading && register && (
                <>
                    <Alert
                        severity={register.applicabilityApplies ? 'warning' : 'info'}
                        sx={{ mb: 2 }}
                        data-testid="insurance-applicability"
                        data-help="statutory.insurance.applicability"
                    >
                        {register.activeMemberCount} active member(s) as of generation. BLA §99 group insurance
                        {register.applicabilityApplies ? ' applies at 100 or more workers.' : ' applies at 100 or more workers, so it is not yet mandatory for you.'}
                    </Alert>

                    <TableContainer component={Paper} sx={{ mb: 2 }} data-testid="insurance-summary-table" data-help-section="statutory.insurance.summary">
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell data-help="statutory.insurance.summary.policyStatus">Policy Status</TableCell>
                                    <TableCell align="right">
                                        <Chip size="small" label={humanizeCode(register.effectiveStatus)} color={statusColor(register.effectiveStatus)} data-testid="insurance-status-chip" />
                                    </TableCell>
                                </TableRow>
                                {register.policy && (
                                    <>
                                        <TableRow>
                                            <TableCell data-help="statutory.insurance.summary.insurer">Insurer</TableCell>
                                            <TableCell align="right">{register.policy.insurerName}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell data-help="statutory.insurance.summary.policyNumber">Policy Number</TableCell>
                                            <TableCell align="right">{register.policy.policyNumber}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell data-help="statutory.insurance.summary.coveragePerWorker">Coverage / Worker</TableCell>
                                            <TableCell align="right">{money(register.policy.coverageAmountPerWorker)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell data-help="statutory.insurance.summary.period">Period</TableCell>
                                            <TableCell align="right">
                                                {register.policy.startDate} — {dash(register.policy.endDate) === '—' ? 'Open-ended' : register.policy.endDate}
                                            </TableCell>
                                        </TableRow>
                                    </>
                                )}
                                <TableRow>
                                    <TableCell data-help="statutory.insurance.summary.generatedAt">Generated At</TableCell>
                                    <TableCell align="right">{formatDateTime(register.generatedAt)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <RegisterNotes notes={register.caveats} testId="insurance-caveats" />

                    <TableContainer component={Paper} sx={{ mb: 2 }} data-testid="insurance-roster-table" data-help-section="statutory.insurance.table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="statutory.insurance.col.employee">Employee</TableCell>
                                    <TableCell data-help="statutory.insurance.col.code">Code</TableCell>
                                    <TableCell data-help="statutory.insurance.col.enrolledOn">Enrolled On</TableCell>
                                    <TableCell align="right" data-help="statutory.insurance.col.coverage">Coverage</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {register.rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            <Typography color="text.secondary">No enrolled members.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {register.rows.map(row => (
                                    <TableRow key={row.userId} hover>
                                        <TableCell>{dash(row.employeeName)}</TableCell>
                                        <TableCell>{dash(row.employeeCode)}</TableCell>
                                        <TableCell>{row.enrolledOn}</TableCell>
                                        <TableCell align="right">{money(row.effectiveCoverage)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Accordion sx={{ mb: 2 }} data-testid="insurance-gaps-section" data-help-section="statutory.insurance.gaps">
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>
                                Disclosed Gaps ({register.notEnrolledCount} not enrolled, {register.staleEnrollmentCount} stale)
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell data-help="statutory.insurance.gaps.col.employee">Not Enrolled: Employee</TableCell>
                                        <TableCell data-help="statutory.insurance.gaps.col.code">Code</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {register.notEnrolledMembers.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={2} align="center">
                                                <Typography color="text.secondary">None.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {register.notEnrolledMembers.map(m => (
                                        <TableRow key={m.userId}>
                                            <TableCell>{dash(m.employeeName)}</TableCell>
                                            <TableCell>{dash(m.employeeCode)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </AccordionDetails>
                    </Accordion>
                </>
            )}
        </Box>
    );
};

export default InsuranceRegisterTab;
